/**
 * Route Context Classifier
 * Determines analytics context based on current route.
 * Used by analytics.ts and sessionRecorder.ts to route events
 * to the correct backend endpoint and DB table.
 */

export type AnalyticsContext = 'marketing' | 'immersive' | 'auth' | 'platform';

const PLATFORM_PREFIXES = ['/platform', '/onboarding'];
const AUTH_PREFIXES = ['/login', '/auth', '/register'];
const IMMERSIVE_PREFIXES = ['/web-chat'];

/**
 * Classify the current page into an analytics context.
 * Order matters: platform > auth > immersive > marketing (default).
 */
export function getRouteContext(pathname?: string): AnalyticsContext {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');

  if (PLATFORM_PREFIXES.some(p => path.startsWith(p))) return 'platform';
  if (AUTH_PREFIXES.some(p => path.startsWith(p))) return 'auth';
  if (IMMERSIVE_PREFIXES.some(p => path.startsWith(p))) return 'immersive';

  return 'marketing';
}

/**
 * Get the analytics events API endpoint for a given context.
 */
export function getEventsEndpoint(context: AnalyticsContext): string {
  return `/api/analytics/${context}/events`;
}

/**
 * Get the replay API endpoint for a given context.
 */
export function getReplayEndpoint(context: AnalyticsContext): string {
  return `/api/analytics/${context}/replay`;
}

/**
 * Check if an event on a marketing page is actually an immersive interaction.
 * The homepage AI chat widget fires demo_query events on '/' but they
 * belong to the immersive context.
 */
export function isImmersiveInteraction(eventName: string, page: string): boolean {
  const immersiveEvents = ['demo_query', 'demo_limit_hit', 'chat_engage', 'video_play'];
  return page === '/' && immersiveEvents.includes(eventName);
}
